import { defineStore } from 'pinia'
import { store } from '@/store'
import { CACHE_KEY, useCache } from '@/hooks/web/useCache'
import request from '@/config/axios'

const { wsCache } = useCache('sessionStorage')

export interface DictDataVO {
  dictType: string
  value: any
  label: string
  colorType?: string
  cssClass?: string
}

export interface DictValueType {
  value: any
  label: string
  colorType?: string
  cssClass?: string
}

export interface DictState {
  dictMap: Map<string, any>
  isSetDict: boolean
}

// 开发环境 Mock：后端未启动时，字典接口直接返回空数组，不发请求
const mockAuth = import.meta.env.DEV && import.meta.env.VITE_MOCK_AUTH === 'true'

// 获取全量简单字典数据（yudao：GET /system/dict-data/list-all-simple）
const getSimpleDictDataList = () => {
  if (mockAuth) return Promise.resolve([] as DictDataVO[])
  return request.get<DictDataVO[]>({ url: '/system/dict-data/list-all-simple' })
}

export const useDictStore = defineStore('dict', {
  state: (): DictState => ({
    dictMap: new Map<string, any>(),
    isSetDict: false
  }),
  getters: {
    getDictMap(): Recordable {
      const dictMap = wsCache.get(CACHE_KEY.DICT_CACHE)
      if (dictMap) {
        this.dictMap = dictMap
      }
      return this.dictMap
    },
    getIsSetDict(): boolean {
      return this.isSetDict
    }
  },
  actions: {
    async setDictMap() {
      const dictMap = wsCache.get(CACHE_KEY.DICT_CACHE)
      if (dictMap) {
        this.dictMap = dictMap
        this.isSetDict = true
      } else {
        // 若接口不可用，捕获异常并跳过，避免阻断登录流程
        let res: DictDataVO[] = []
        try {
          res = await getSimpleDictDataList()
        } catch (error) {
          this.isSetDict = true
          return
        }
        if (!res || res.length === 0) {
          this.isSetDict = true
          return
        }
        const dictDataMap = new Map<string, any>()
        res.forEach((dictData: DictDataVO) => {
          const enumValueObj = dictDataMap[dictData.dictType]
          if (!enumValueObj) {
            dictDataMap[dictData.dictType] = []
          }
          dictDataMap[dictData.dictType].push({
            value: dictData.value,
            label: dictData.label,
            colorType: dictData.colorType,
            cssClass: dictData.cssClass
          })
        })
        this.dictMap = dictDataMap
        this.isSetDict = true
        wsCache.set(CACHE_KEY.DICT_CACHE, dictDataMap, { exp: 60 }) // 60 秒过期
      }
    },
    getDictByType(type: string) {
      if (!this.isSetDict) {
        this.setDictMap()
      }
      return this.dictMap[type]
    }
  }
})

export const useDictStoreWithOut = () => {
  return useDictStore(store)
}
